import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { 
    ArrowLeft,
    Send,
    Eye,
    BarChart3,
    Users,
    Mail,
    MousePointerClick,
    TrendingUp,
    ShoppingCart,
    Calendar,
    Clock,
    Loader2,
    CheckCircle,
    XCircle,
    Copy,
    RefreshCw,
    AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CampaignDetails = () => {
    const { campaignId } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [campaign, setCampaign] = useState(null);
    const [stats, setStats] = useState(null);
    const [sending, setSending] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');

    const fetchCampaign = useCallback(async () => {
        try {
            const [campaignRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/admin/campaigns/${campaignId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/admin/campaigns/${campaignId}/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setCampaign(campaignRes.data);
            setStats(statsRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [token, campaignId]);

    useEffect(() => {
        fetchCampaign();
        
        // Auto-refresh for sending campaigns
        const interval = setInterval(() => {
            if (campaign?.status === 'sending') {
                fetchCampaign();
            }
        }, 5000);
        
        return () => clearInterval(interval);
    }, [fetchCampaign, campaign?.status]);

    const getStatusBadge = (status) => {
        const configs = {
            'draft': { bg: 'bg-slate-100', text: 'text-slate-700', icon: Clock, label: 'Brouillon' },
            'scheduled': { bg: 'bg-amber-100', text: 'text-amber-700', icon: Calendar, label: 'Programmée' },
            'sending': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Loader2, label: 'En cours d\'envoi' },
            'sent': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Envoyée' },
            'partial': { bg: 'bg-orange-100', text: 'text-orange-700', icon: AlertCircle, label: 'Envoi partiel' }
        };
        const config = configs[status] || configs.draft;
        const Icon = config.icon;
        return (
            <Badge className={`${config.bg} ${config.text} flex items-center gap-1`}>
                <Icon className={`w-3 h-3 ${status === 'sending' ? 'animate-spin' : ''}`} />
                {config.label}
            </Badge>
        );
    };

    const getCampaignTypeLabel = (type) => {
        const labels = {
            'new_arrivals': 'Nouveaux arrivages',
            'opportunities': 'Opportunités',
            'promotions': 'Promotions',
            'stock_wanted': 'Stock recherché',
            'free': 'Campagne libre'
        };
        return labels[type] || type;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleSend = async () => {
        if (!window.confirm(`Envoyer cette campagne à ${campaign.recipients?.length || 0} destinataires ?`)) return;
        
        setSending(true);
        try {
            await axios.post(
                `${API_URL}/admin/campaigns/${campaignId}/send`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Envoi démarré');
            fetchCampaign();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setSending(false);
        }
    };

    const handleDuplicate = async () => {
        try {
            const response = await axios.post(
                `${API_URL}/admin/campaigns/${campaignId}/duplicate`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Campagne dupliquée');
            navigate(`/admin/communication/campaign/${response.data.campaign.id}`);
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handlePreview = async () => {
        try {
            const response = await axios.get(
                `${API_URL}/admin/campaigns/${campaignId}/preview-email`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPreviewHtml(response.data.html);
            setShowPreview(true);
        } catch (error) {
            toast.error('Erreur');
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
            </AdminLayout>
        );
    }

    if (!campaign) {
        return (
            <AdminLayout>
                <div className="text-center py-12">
                    <p className="text-slate-500">Campagne non trouvée</p>
                    <Button className="mt-4" onClick={() => navigate('/admin/communication')}>
                        Retour
                    </Button>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/communication')}>
                            <ArrowLeft className="w-4 h-4 mr-1" />
                            Retour
                        </Button>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-2xl font-bold">{campaign.name}</h1>
                                {getStatusBadge(campaign.status)}
                            </div>
                            <p className="text-slate-500">{getCampaignTypeLabel(campaign.campaign_type)}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={handlePreview}>
                            <Eye className="w-4 h-4 mr-2" />
                            Prévisualiser
                        </Button>
                        <Button variant="outline" onClick={handleDuplicate}>
                            <Copy className="w-4 h-4 mr-2" />
                            Dupliquer
                        </Button>
                        {campaign.status === 'draft' && (
                            <Button onClick={handleSend} disabled={sending} className="bg-green-600 hover:bg-green-700">
                                {sending ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4 mr-2" />
                                )}
                                Envoyer
                            </Button>
                        )}
                        <Button variant="outline" onClick={fetchCampaign}>
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Sending Progress */}
                {campaign.status === 'sending' && campaign.send_progress && (
                    <Card className="border-blue-200 bg-blue-50">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-4">
                                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                                <div className="flex-1">
                                    <p className="font-medium text-blue-900">Envoi en cours...</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <div className="flex-1 h-2 bg-blue-200 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-blue-600 transition-all"
                                                style={{ width: `${(campaign.send_progress.sent / campaign.send_progress.total) * 100}%` }}
                                            />
                                        </div>
                                        <span className="text-sm text-blue-700">
                                            {campaign.send_progress.sent} / {campaign.send_progress.total}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <Send className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.stats?.sent || 0}</p>
                                        <p className="text-xs text-slate-500">Envoyés</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-green-100 rounded-lg">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.stats?.delivered || 0}</p>
                                        <p className="text-xs text-slate-500">Délivrés</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-purple-100 rounded-lg">
                                        <Eye className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.stats?.opened || 0}</p>
                                        <p className="text-xs text-slate-500">Ouvertures</p>
                                        <p className="text-xs text-purple-600">{stats.stats?.open_rate || 0}%</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-amber-100 rounded-lg">
                                        <MousePointerClick className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.stats?.clicked || 0}</p>
                                        <p className="text-xs text-slate-500">Clics</p>
                                        <p className="text-xs text-amber-600">{stats.stats?.click_rate || 0}%</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-indigo-100 rounded-lg">
                                        <ShoppingCart className="w-5 h-5 text-indigo-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.stats?.orders_generated || 0}</p>
                                        <p className="text-xs text-slate-500">Commandes</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{stats.stats?.revenue_generated?.toLocaleString() || 0}€</p>
                                        <p className="text-xs text-slate-500">CA généré</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Campaign Info */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Détails de la campagne</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <p className="text-sm text-slate-500">Objet</p>
                                <p className="font-medium">{campaign.subject}</p>
                            </div>
                            {campaign.preheader && (
                                <div>
                                    <p className="text-sm text-slate-500">Pré-header</p>
                                    <p>{campaign.preheader}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-sm text-slate-500">Texte d'introduction</p>
                                <p className="text-sm">{campaign.intro_text}</p>
                            </div>
                            <div className="flex gap-4 text-sm">
                                <div>
                                    <p className="text-slate-500">Créée le</p>
                                    <p>{formatDate(campaign.created_at)}</p>
                                </div>
                                {campaign.send_completed_at && (
                                    <div>
                                        <p className="text-slate-500">Envoyée le</p>
                                        <p>{formatDate(campaign.send_completed_at)}</p>
                                    </div>
                                )}
                                {campaign.scheduled_at && campaign.status === 'scheduled' && (
                                    <div>
                                        <p className="text-slate-500">Programmée pour</p>
                                        <p className="text-amber-600 font-medium">{formatDate(campaign.scheduled_at)}</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Products */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">
                                Produits ({campaign.products?.length || 0})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {campaign.products?.map((product, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                        <div>
                                            <p className="font-medium text-sm">{product.brand} {product.model}</p>
                                            <p className="text-xs text-slate-500">
                                                {product.storage_capacity}GB • Grade {product.grade}
                                            </p>
                                        </div>
                                        <p className="font-bold text-blue-600">{product.price?.toFixed(2)}€</p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Recipients */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Destinataires ({campaign.recipients?.length || 0})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                            {campaign.recipients?.map((recipient, idx) => (
                                <div key={idx} className="p-2 bg-slate-50 rounded text-sm">
                                    <p className="font-medium truncate">{recipient.company_name || recipient.full_name}</p>
                                    <p className="text-slate-500 truncate">{recipient.email}</p>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Preview Modal */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
                    <DialogHeader>
                        <DialogTitle>Prévisualisation de l'email</DialogTitle>
                    </DialogHeader>
                    <div 
                        className="border rounded-lg overflow-auto bg-white"
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPreview(false)}>
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
};

export default CampaignDetails;
